import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const closeMenu = () => setMenuOpen(false);

  if (isAuthenticated) {
    return <Navigate to={user?.is_admin ? '/admin' : '/dashboard'} replace />;
  }

  const services = t('home.services.list', { returnObjects: true }) as { title: string, desc: string }[];
  const journeySteps = t('home.journey.steps', { returnObjects: true }) as { title: string, copy: string }[];
  const audienceGroups = t('home.audience.groups', { returnObjects: true }) as { title: string, desc: string, cta: string, ctaLink: string }[];
  const advantagesList = t('home.advantages.list', { returnObjects: true }) as { title: string, desc: string }[];

  const serviceIcons = [
    'fa-solid fa-file-invoice-dollar',
    'fa-solid fa-credit-card',
    'fa-solid fa-box-open',
    'fa-solid fa-eye',
    'fa-solid fa-handshake-angle'
  ];

  const navLinks = (
    <>
      <a href="#services" onClick={closeMenu}>{t('common.services')}</a>
      <a href="#journey" onClick={closeMenu}>{t('home.journey.title')}</a>
      <a href="#advantage" onClick={closeMenu}>{t('common.advantages')}</a>
      <Link to="/login" onClick={closeMenu}>{t('common.login')}</Link>
      <Link to="/register" className="home-nav-cta" onClick={closeMenu}>{t('common.register')}</Link>
      <LanguageSwitcher />
    </>
  );

  return (
    <main className="home-page">
      <nav className="home-nav">
        <Link to="/" className="home-brand">{t('home.brand')}</Link>

        <div className="home-nav-links desktop-nav">
          {navLinks}
        </div>

        <Link to="/login" className="home-sign-btn">
          {t('common.signUp')} / {t('common.login')}
        </Link>

        <button
          className="hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`} />
        </button>

        {menuOpen && <div className={`mobile-overlay ${menuOpen ? 'show' : ''}`} onClick={closeMenu} />}
        <div className={`mobile-nav ${menuOpen ? 'open' : ''}`}>
          {navLinks}
        </div>
      </nav>

      <section className="home-hero" id="home">
        <div className="hero-texture"></div>
        <div className="container">
          <div className="hero-eyebrow">
            <span className="home-brand">{t('home.brand')}</span>
            <span className="home-kicker">{t('home.hero.tagline')}</span>
          </div>
          <h1>{t('home.hero.title')}</h1>

          <h2 className="home-hero-subheadline">
            {t('home.hero.subHeadline')}
          </h2>

          <div className="home-hero-actions">
            <Link to="/quote" className="btn btn-primary">
              {t('home.hero.cta1')}
            </Link>
            <Link to="/membership" className="btn btn-hero-outline">
              {t('home.hero.cta2')}
            </Link>
          </div>
        </div>
      </section>



      <section className="home-section" id="services">
        <div className="container">
          <h2>{t('home.services.title')}</h2>
          <div className="home-service-grid">
            {services.map((service, index) => (
              <div key={index} className="home-service-card">
                <i className={serviceIcons[index % serviceIcons.length]}></i>
                <h3>{service.title}</h3>
                <p className="home-service-desc">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section" id="journey">
        <div className="container">
          <h2 style={{ textAlign: 'left', marginBottom: '60px' }}>{t('home.journey.title')}</h2>
          <div className="home-step-grid">
            {journeySteps.map((step, index) => (
              <React.Fragment key={index}>
                <div className="home-step-card">
                  <div className="home-step-icon-wrap">
                    <div className="home-step-badge">{String(index + 1).padStart(2, '0')}</div>
                    {index === 0 ? (
                      <svg viewBox="0 0 64 64" width="52" height="52" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <rect x="12" y="10" width="28" height="34" rx="3" strokeWidth="1.8"/>
                        <path d="M16 18h20" strokeWidth="1.8" strokeLinecap="round"/>
                        <path d="M16 26h16" strokeWidth="1.8" strokeLinecap="round"/>
                        <path d="M16 34h12" strokeWidth="1.8" strokeLinecap="round"/>
                        <circle cx="47" cy="47" r="8" strokeWidth="1.8"/>
                        <path d="M52 52l6 6" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="47" cy="47" r="3" fill="currentColor" opacity="0.12" stroke="none"/>
                      </svg>
                    ) : index === 1 ? (
                      <svg viewBox="0 0 64 64" width="52" height="52" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <rect x="12" y="28" width="40" height="28" rx="4" strokeWidth="1.8"/>
                        <rect x="16" y="28" width="32" height="6" rx="1" fill="currentColor" opacity="0.1" stroke="none"/>
                        <path d="M22 42h6" strokeWidth="1.8" strokeLinecap="round"/>
                        <path d="M22 48h12" strokeWidth="1.8" strokeLinecap="round"/>
                        <path d="M32 10l-8 14h16L32 10z" strokeWidth="1.8" strokeLinejoin="round"/>
                        <path d="M32 10v6" strokeWidth="1.8" strokeLinecap="round"/>
                        <circle cx="32" cy="10" r="3" fill="currentColor" opacity="0.08" stroke="none"/>
                      </svg>
                    ) : index === 2 ? (
                      <svg viewBox="0 0 64 64" width="52" height="52" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <circle cx="32" cy="30" r="18" strokeWidth="1.8"/>
                        <path d="M20 30l8 8 16-16" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 50c0-6 8-10 18-10s18 4 18 10" strokeWidth="1.6" strokeLinecap="round"/>
                        <circle cx="20" cy="14" r="4" strokeWidth="1.2" fill="currentColor" opacity="0.08" stroke="none"/>
                        <circle cx="44" cy="14" r="4" strokeWidth="1.2" fill="currentColor" opacity="0.08" stroke="none"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 64 64" width="52" height="52" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <rect x="10" y="14" width="44" height="36" rx="4" strokeWidth="1.8"/>
                        <path d="M18 14V8h28v6" strokeWidth="1.8"/>
                        <path d="M22 28l8 8 16-16" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="20" cy="44" r="3" fill="currentColor" opacity="0.12" stroke="none"/>
                        <circle cx="44" cy="44" r="3" fill="currentColor" opacity="0.12" stroke="none"/>
                        <path d="M32 50v-6" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
                      </svg>
                    )}
                  </div>
                  <div className="home-step-content">
                    <h3>{step.title}</h3>
                    <p>{step.copy}</p>
                  </div>
                </div>
                {index < journeySteps.length - 1 && (
                  <div className="home-step-connector">
                    <svg viewBox="0 0 48 24" width="36" height="18" fill="none" stroke="var(--accent-gold)" strokeWidth="1.5">
                      <path d="M8 12h28" strokeLinecap="round"/>
                      <path d="M28 4l8 8-8 8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-audience" id="audience">
        <div className="container">
          <h2 style={{ textAlign: 'left' }}>{t('home.audience.title')}</h2>
          <div className="home-audience-grid">
            {audienceGroups.map((group, index) => (
              <div key={index} className="home-audience-card">
                <h3>{group.title}</h3>
                <p className="audience-desc">{group.desc}</p>
                <button
                  className="audience-cta"
                  onClick={() => navigate(group.ctaLink)}
                >
                  {group.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section" id="advantage">
        <div className="container">
          <h2 style={{ textAlign: 'left' }}>{t('home.advantages.title')}</h2>
          
          <div className="home-advantages-grid">
            <div>
              {advantagesList.slice(0, 2).map((item, index) => (
                <div key={index} className="advantage-item">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
            <div>
              {advantagesList.slice(2).map((item, index) => (
                <div key={index} className="advantage-item">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-mission">
        <div className="container">
          <h2>{t('home.mission.title')}</h2>
          <p>{t('home.mission.text')}</p>
        </div>
      </section>

      <section className="home-partner-section" id="partner">
        <div className="container">
          <div className="home-partner-inner">
            <h2>{t('home.portal.partnerCta')}</h2>
            <Link to="/partner-program" className="home-partner-btn">
              Learn More & Register →
            </Link>
          </div>
        </div>
      </section>

      <footer className="home-footer">
        <div className="container">
          <div className="home-footer-trust">
            <div className="trust-badge-row">
              <span className="trust-badge">
                <i className="fa-solid fa-lock"></i> <span className="trust-label">256-bit SSL</span>
              </span>
              <span className="trust-badge">
                <i className="fa-brands fa-cc-stripe"></i> <span className="trust-label">Stripe Payments</span>
              </span>
              <span className="trust-badge">
                <i className="fa-solid fa-building-columns"></i> <span className="trust-label">UK Company 12106600</span>
              </span>
            </div>
          </div>

          <div className="footer-grid">
            <div className="footer-col-left">
              <strong>{t('home.brand')}</strong>
              <p className="footer-company">{t('home.footer.companyInfo')}</p>
              <p className="footer-address">{t('home.footer.address')}</p>
            </div>
            <div className="footer-col-right">
              <div className="footer-links">
                <Link to="/privacy">Privacy Policy</Link>
                <span className="footer-sep">|</span>
                <Link to="/terms">Terms of Service</Link>
                <span className="footer-sep">|</span>
                <Link to="/shipping">Shipping & Taxes</Link>
                <span className="footer-sep">|</span>
                <span className="footer-link-static">FCA Trade Terms</span>
                <span className="footer-sep">|</span>
                <Link to="/settings/help">FAQs</Link>
              </div>
              <p className="footer-copy">{t('home.footer.copyright')}</p>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Home;
